<template>
  <div class="v2-ship-step">
    <h3 class="v2-ship-step__title">
      Select Orders
    </h3>
    <p class="v2-ship-step__subtitle">
      Orders containing your selected products that are eligible for shipment.
    </p>

    <div
      v-if="isLoading"
      class="v2-ship-step__loader"
    >
      <v2-spinner size="md" />
    </div>

    <template v-else>
      <div
        v-if="!orders.length"
        class="v2-ship-step__empty"
      >
        No eligible orders found for the selected products.
      </div>

      <template v-else>
        <div class="v2-ship-step__filters">
          <v2-input
            :value="searchQuery"
            placeholder="Search by retailer or order IDs (comma-separated)..."
            class="v2-ship-step__filter-search"
            @input="searchQuery = $event"
          />
          <v2-multi-select
            :value="selectedRetailers"
            :option-groups="retailerOptionGroups"
            placeholder="Select retailer..."
            class="v2-ship-step__filter-multi"
            @input="onRetailersChange"
          />
        </div>

        <div class="v2-ship-step__toolbar">
          <v2-checkbox
            :value="allFilteredSelected"
            :label="`Select All (${filteredOrders.length})`"
            @input="toggleAll"
          />
          <span class="v2-ship-step__count">
            {{ localSelectedIds.length }} of {{ orders.length }} orders selected
          </span>
        </div>

        <div class="v2-ship-step__table-wrap">
          <v2-table
            :columns="tableColumns"
            :items="filteredOrders"
            :clickable-rows="true"
            @row-click="toggleOrder"
          >
            <template #cell(selected)="{ item }">
              <!--
                click.stop wrapper — V2Table's @row-click also fires
                toggleOrder when the row is clicked, so without stopping
                propagation here the native checkbox click bubbles to the
                row, the two invocations cancel out, and selection never
                changes. V1 used `@click.native.stop` on b-form-checkbox.
              -->
              <span @click.stop>
                <v2-checkbox
                  :value="localSelectedIds.includes(item._id)"
                  @input="toggleOrder(item)"
                />
              </span>
            </template>
            <template #cell(eligibleProductCount)="{ item }">
              {{ item.eligibleProductCount }} / {{ item.totalProductCount }}
            </template>
            <template #empty>
              No orders match the current filters.
            </template>
          </v2-table>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import store from '@/store'
import { GET_BULK_SHIP_ELIGIBLE_ORDERS } from '@/store/modules/order.module'
import V2Input from '@/components/v2/V2Input.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2MultiSelect from '@/components/v2/V2MultiSelect.vue'

export default {
  name: 'V2ShipAllOrderStep',
  components: {
    V2Input,
    V2Checkbox,
    V2Spinner,
    V2Table,
    V2MultiSelect,
  },
  props: {
    collectionId: { type: String, required: true },
    productIds: { type: Array, required: true },
    selectedOrderIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      orders: [],
      localSelectedIds: [...this.selectedOrderIds],
      searchQuery: '',
      selectedRetailers: [],
      tableColumns: [
        { key: 'selected', label: '', width: '40px' },
        { key: 'orderSeqId', label: 'Order ID' },
        { key: 'retailerName', label: 'Retailer' },
        { key: 'eligibleProductCount', label: 'Eligible Products' },
      ],
    }
  },
  computed: {
    uniqueRetailerNames() {
      const seen = new Set()
      this.orders.forEach(o => { if (o.retailerName) seen.add(o.retailerName) })
      return Array.from(seen).sort((a, b) => a.localeCompare(b))
    },
    retailerOptionGroups() {
      return [{ title: '', options: this.uniqueRetailerNames }]
    },
    filteredOrders() {
      let filtered = this.orders
      if (this.selectedRetailers.length) {
        filtered = filtered.filter(o => this.selectedRetailers.includes(o.retailerName))
      }
      if (this.searchQuery) {
        const terms = this.searchQuery
          .split(',')
          .map(t => t.trim().toLowerCase())
          .filter(Boolean)
        if (terms.length) {
          filtered = filtered.filter(o => terms.some(q => (o.retailerName || '').toLowerCase().includes(q)
              || (o.orderSeqId || '').toLowerCase().includes(q)))
        }
      }
      return filtered
    },
    allFilteredSelected() {
      return this.filteredOrders.length > 0
        && this.filteredOrders.every(o => this.localSelectedIds.includes(o._id))
    },
  },
  watch: {
    productIds: {
      immediate: true,
      handler() {
        this.loadOrders()
      },
    },
  },
  methods: {
    async loadOrders() {
      if (!this.collectionId || !this.productIds.length) return
      this.isLoading = true
      try {
        const res = await store.dispatch(GET_BULK_SHIP_ELIGIBLE_ORDERS, {
          collectionId: this.collectionId,
          productIds: this.productIds,
        })
        this.orders = res?.data?.data?.orders || []
        // Mirror V1: auto-select all orders on first load
        if (this.localSelectedIds.length === 0) {
          this.localSelectedIds = this.orders.map(o => o._id)
          this.$emit('update', [...this.localSelectedIds])
        }
      } catch {
        this.orders = []
      } finally {
        this.isLoading = false
      }
    },
    onRetailersChange(values) {
      this.selectedRetailers = Array.isArray(values) ? values : []
    },
    toggleOrder(item) {
      const idx = this.localSelectedIds.indexOf(item._id)
      if (idx >= 0) {
        this.localSelectedIds.splice(idx, 1)
      } else {
        this.localSelectedIds.push(item._id)
      }
      this.$emit('update', [...this.localSelectedIds])
    },
    toggleAll(checked) {
      if (checked) {
        const ids = this.filteredOrders.map(o => o._id)
        this.localSelectedIds = [...new Set([...this.localSelectedIds, ...ids])]
      } else {
        const ids = new Set(this.filteredOrders.map(o => o._id))
        this.localSelectedIds = this.localSelectedIds.filter(id => !ids.has(id))
      }
      this.$emit('update', [...this.localSelectedIds])
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-ship-step {
  padding: var(--v2-space-2) var(--v2-space-1);

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0 0 var(--v2-space-2) 0;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin: 0 0 var(--v2-space-4) 0;
  }

  &__loader {
    display: flex;
    justify-content: center;
    padding: var(--v2-space-8) 0;
  }

  &__empty {
    text-align: center;
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
    padding: var(--v2-space-8) 0;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-3);
  }

  &__filter-search {
    flex: 1 1 60%;
    min-width: 0;
  }

  &__filter-multi {
    flex: 1 1 35%;
    min-width: 0;
  }

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--v2-space-2);
  }

  &__count {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    font-weight: 600;
  }

  &__table-wrap {
    max-height: 350px;
    overflow-y: auto;
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
  }
}

// White retailer chip — the shared V2MultiSelect ships a high-contrast
// dark pill (foreground on background) which reads as out-of-theme against
// the light Ship All modal. Override the chip styling locally with a plain
// white surface, foreground text, and a soft glass-stroke border so the
// chip blends with the v2 modal's light theme. !important is required to
// beat the scoped-style specificity inside V2MultiSelect itself. Other
// consumers of V2MultiSelect keep the original dark chip.
::v-deep .v2-ship-step__filter-multi {
  .v2-multiselect__tag {
    background: #ffffff !important;
    color: var(--v2-foreground) !important;
    border: 1px solid var(--v2-glass-stroke) !important;
    box-shadow: 0 1px 2px oklch(0 0 0 / 0.04);
  }

  .v2-multiselect__tag-text {
    color: var(--v2-foreground) !important;
  }

  .v2-multiselect__tag-remove {
    color: var(--v2-muted-foreground) !important;
    opacity: 0.7;

    &:hover {
      opacity: 1;
      color: var(--v2-foreground) !important;
    }
  }
}
</style>
